// ./src/config/currencyOptions.ts
// Currency options configuration for user preferences
// Based on ISO 4217 currency codes
// Priority currencies (USD, CAD, EUR) appear at the top

// ⚠ A symbol says WHICH currency, never just "a dollar" (owner, 2026-10-03: "always show what type of currency it is.
//   For USD we need US$"). USD is "US$", CAD "CA$", and a symbol several currencies share ("¥", "kr", "﷼") is written as
//   the code instead. Every symbol in this table is unique — `npm run check-money-label` fails on a duplicate or a bare
//   "$". ⓘ It is the PICKER's and the price input's prefix; amounts themselves go through `formatCurrency`, which prints
//   Intl's symbol for the reader's locale (with the same never-a-bare-"$" rule), so the two can differ (S$ / SGD).
export interface CurrencyOption {
  value: string; // ISO 4217 code
  label: string; // Full name
  symbol: string; // Currency symbol
  priority?: boolean; // Show at top of list
}

// Priority currencies - always shown at top
export const PRIORITY_CURRENCIES: CurrencyOption[] = [
  { value: "USD", label: "US Dollar", symbol: "US$", priority: true },
  { value: "EUR", label: "Euro", symbol: "€", priority: true },
  { value: "CAD", label: "Canadian Dollar", symbol: "CA$", priority: true },
];

// Common world currencies
export const COMMON_CURRENCIES: CurrencyOption[] = [
  { value: "GBP", label: "British Pound", symbol: "£" },
  { value: "JPY", label: "Japanese Yen", symbol: "¥" },
  { value: "AUD", label: "Australian Dollar", symbol: "A$" },
  { value: "CHF", label: "Swiss Franc", symbol: "CHF" },
  { value: "CNY", label: "Chinese Yuan", symbol: "CN¥" },
  { value: "INR", label: "Indian Rupee", symbol: "₹" },
  { value: "MXN", label: "Mexican Peso", symbol: "MX$" },
  { value: "BRL", label: "Brazilian Real", symbol: "R$" },
  { value: "RUB", label: "Russian Ruble", symbol: "₽" },
  { value: "BYN", label: "Belarussian Ruble", symbol: "p" },
  { value: "KRW", label: "South Korean Won", symbol: "₩" },
  { value: "SGD", label: "Singapore Dollar", symbol: "S$" },
  { value: "HKD", label: "Hong Kong Dollar", symbol: "HK$" },
  { value: "NOK", label: "Norwegian Krone", symbol: "NOK" },
  { value: "SEK", label: "Swedish Krona", symbol: "SEK" },
  { value: "DKK", label: "Danish Krone", symbol: "DKK" },
  { value: "NZD", label: "New Zealand Dollar", symbol: "NZ$" },
  { value: "ZAR", label: "South African Rand", symbol: "R" },
  { value: "PLN", label: "Polish Złoty", symbol: "zł" },
  { value: "TRY", label: "Turkish Lira", symbol: "₺" },
  { value: "THB", label: "Thai Baht", symbol: "฿" },
  { value: "IDR", label: "Indonesian Rupiah", symbol: "Rp" },
  { value: "MYR", label: "Malaysian Ringgit", symbol: "RM" },
  { value: "PHP", label: "Philippine Peso", symbol: "₱" },
  { value: "CZK", label: "Czech Koruna", symbol: "Kč" },
  { value: "ILS", label: "Israeli Shekel", symbol: "₪" },
  { value: "CLP", label: "Chilean Peso", symbol: "CL$" },
  { value: "AED", label: "UAE Dirham", symbol: "د.إ" },
  { value: "SAR", label: "Saudi Riyal", symbol: "SAR" },
  { value: "TWD", label: "Taiwan Dollar", symbol: "NT$" },
  { value: "ARS", label: "Argentine Peso", symbol: "AR$" },
  { value: "COP", label: "Colombian Peso", symbol: "CO$" },
  { value: "PEN", label: "Peruvian Sol", symbol: "S/" },
  { value: "VND", label: "Vietnamese Dong", symbol: "₫" },
  { value: "UAH", label: "Ukrainian Hryvnia", symbol: "₴" },
  { value: "RON", label: "Romanian Leu", symbol: "lei" },
  { value: "HUF", label: "Hungarian Forint", symbol: "Ft" },
  { value: "BGN", label: "Bulgarian Lev", symbol: "лв" },
  { value: "HRK", label: "Croatian Kuna", symbol: "kn" },
  { value: "ISK", label: "Icelandic Króna", symbol: "ISK" },
  { value: "EGP", label: "Egyptian Pound", symbol: "E£" },
  { value: "PKR", label: "Pakistani Rupee", symbol: "₨" },
  { value: "BDT", label: "Bangladeshi Taka", symbol: "৳" },
  { value: "NGN", label: "Nigerian Naira", symbol: "₦" },
  { value: "KES", label: "Kenyan Shilling", symbol: "KSh" },
  { value: "GHS", label: "Ghanaian Cedi", symbol: "₵" },
  { value: "MAD", label: "Moroccan Dirham", symbol: "د.م." },
  { value: "QAR", label: "Qatari Riyal", symbol: "QAR" },
  { value: "KWD", label: "Kuwaiti Dinar", symbol: "د.ك" },
  { value: "BHD", label: "Bahraini Dinar", symbol: "BD" },
  { value: "OMR", label: "Omani Rial", symbol: "OMR" },
  { value: "JOD", label: "Jordanian Dinar", symbol: "JD" },
  { value: "LBP", label: "Lebanese Pound", symbol: "L£" },
];

// Combined list with priority currencies first
export const CURRENCY_OPTIONS: CurrencyOption[] = [
  ...PRIORITY_CURRENCIES,
  ...COMMON_CURRENCIES.sort((a, b) => a.label.localeCompare(b.label)),
];

// Map for quick lookup
export const CURRENCY_MAP: Record<string, CurrencyOption> = Object.fromEntries(
  CURRENCY_OPTIONS.map((opt) => [opt.value, opt])
);

// Helper to get display label for select
// ⓘ A symbol that IS the code ("NOK") is not repeated ("NOK NOK - Norwegian Krone").
export const getCurrencyDisplayLabel = (currency: CurrencyOption): string => {
  return currency.symbol === currency.value ? `${currency.value} - ${currency.label}` : `${currency.symbol} ${currency.value} - ${currency.label}`;
};

// Helper to get short display label
export const getCurrencyShortLabel = (currency: CurrencyOption): string => {
  return currency.symbol === currency.value ? currency.value : `${currency.symbol} ${currency.value}`;
};
